import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { UserDetails } from "../../models/UserDetails";
import { getDynamicTextColors, getUserBalance } from "../../store/authActions";
import { domain } from "../../constants/Branding";
import { getEnvVariable } from "../../constants/whitelabelEnv";
import BONUS_API from "../../services/bonus";
import { toast } from "react-toastify";
import partyPopIcon from "../../assets/images/partypop.svg";
import "./SpinTab.scss";

interface SpinTabProps {
  userDetails: UserDetails;
  dynamicTextColors?: any;
}

interface IncentiveAvailableSpinOutcome {
  reward_type: string;
  value_type: string;
  value: number;
}

interface IncentiveAvailableSpinItem {
  user_incentive_id: string;
  transaction_id: string;
  policy_name: string;
  attempts_available: number;
  expiry_at: number;
  status: string;
  outcomes: IncentiveAvailableSpinOutcome[];
}

/** POST /incentives/{incentive_id}/spin — response includes outcome + nested reward */
interface IncentiveSpinReward {
  type: string;
  amount: number;
  /** Legacy/alternate field names */
  reward_type?: string;
  reward_amount?: number;
}

interface IncentiveSpinResponse {
  outcome_id: string;
  reward: IncentiveSpinReward;
}

interface IncentiveSpinCompleteResponse {
  outcome_id: string;
  status: string;
}

function getSpinRewardNumericAmount(reward: IncentiveSpinReward): number | undefined {
  const raw = reward.amount ?? reward.reward_amount;
  if (raw === undefined || raw === null) return undefined;
  const n = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

function getSpinRewardTypeKey(reward: IncentiveSpinReward): string | undefined {
  const t = reward.type ?? reward.reward_type;
  return typeof t === "string" && t.length > 0 ? t : undefined;
}

interface IncentiveOutcomeDetailResponse {
  user_incentive_id: string;
  /** Claim uses POST /incentives/{user_incentive_id}/outcomes/{outcome_id}/claim */
  outcome_id?: string;
  reward_type: string;
  reward_amount: number;
  required_wager: number;
  created_at: number;
  status: string;
}

interface IncentiveOutcomeListResponse {
  entries: IncentiveOutcomeDetailResponse[];
}

function getOutcomeEntryId(entry: IncentiveOutcomeDetailResponse): string | null {
  const e = entry as IncentiveOutcomeDetailResponse & {
    id?: string;
    user_outcome_id?: string;
  };
  const raw = e.outcome_id ?? e.id ?? e.user_outcome_id;
  return typeof raw === "string" && raw.length > 0 ? raw : null;
}

/** Single rAF-driven spin session (no cancel/restart between phases = smooth motion) */
interface SpinMotionSession {
  cancelled: boolean;
  phase: "free" | "coast";
  lastNow: number;
  spinT0: number;
  pendingTargetIndex: number | null;
  resolvedIdx: number;
  coastT0: number;
  coastStart: number;
  coastEnd: number;
  coastDurationMs: number;
}

/** Full-speed spin after click before easing to wedge (API is called immediately; may return sooner) */
const SPIN_MIN_FREE_SPIN_MS = 4500;
/** After outcome: ease-out duration clamps; base duration matches free-spin speed at t=0 (cubic d/dt=3) */
const SPIN_SETTLE_MS_MIN = 2400;
const SPIN_SETTLE_MS_MAX = 6800;
/** Leading coefficient of easeOutCubic at t=0 (derivative of 1-(1-t)^³) */
const SPIN_SETTLE_EASE_START_SLOPE = 3;
/** Brief pause after wheel stops before win-reveal sequence */
const SPIN_POPUP_DELAY_MS = 120;
/** Logo → “You won” reveal on wheel; then claim popup (keep in sync with SCSS animation) */
const SPIN_WIN_REVEAL_MS = 3000;
/** deg/ms during free spin (requestAnimationFrame); ~150°/s */
const FREE_SPIN_DEG_PER_MS = 0.42;

/** Softer, longer-feeling slowdown than quint (braking spreads over more of the arc) */
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

const CONFETTI_BIT_COUNT = 96;
const WHEEL_BULB_COUNT = 20;

/** Shown on wheel slices when there are no ACTIVE spin incentives (display only). */
const DEMO_WHEEL_LABEL_POOL = [
  "25",
  "50",
  "100",
  "5%",
  "10%",
  "2x",
  "75",
  "150",
  "500",
  "VIP",
  "Bonus",
  "200",
];

/** Tumbling confetti blast; loops until Claim / dismiss */
function ConfettiCelebrationBlast({ variant }: { variant: "stage" | "popup" }) {
  return (
    <div
      className={`spin-tab__confetti-celebration spin-tab__confetti-celebration--${variant}`}
      aria-hidden
    >
      {Array.from({ length: CONFETTI_BIT_COUNT }, (_, i) => {
        const golden = (i * 137.508) % 360;
        const rad = (golden * Math.PI) / 180;
        const dist = 72 + (i % 14) * 10;
        const tx = Math.sin(rad) * dist;
        const ty = -Math.cos(rad) * dist;
        const gy = 28 + (i % 9) * 9;
        const delay = ((i * 0.0293) % 2.15).toFixed(3);
        const dur = (1.72 + (i % 8) * 0.13).toFixed(2);
        const hue = (i * 37 + (i % 7) * 19) % 360;
        const w = 4 + (i % 5);
        const h = 5 + (i % 5) * 2;
        const spin = 180 + (i % 11) * 55;
        return (
          <span
            key={`cb-${i}`}
            className="spin-tab__confetti-bit"
            style={
              {
                "--cf-tx": `${tx.toFixed(1)}px`,
                "--cf-ty": `${ty.toFixed(1)}px`,
                "--cf-gy": `${gy}px`,
                "--cf-delay": `${delay}s`,
                "--cf-dur": `${dur}s`,
                "--cf-hue": String(hue),
                "--cf-w": `${w}px`,
                "--cf-h": `${h}px`,
                "--cf-spin": `${spin}deg`,
              } as React.CSSProperties
            }
          />
        );
      })}
    </div>
  );
}

const SpinTab: React.FC<SpinTabProps> = ({ userDetails, dynamicTextColors }) => {
  const [rotationDeg, setRotationDeg] = useState<number>(0);
  const [isSpinning, setIsSpinning] = useState<boolean>(false);
  const [lastResult, setLastResult] = useState<string>("");
  const [highlightedSection, setHighlightedSection] = useState<number | null>(null);
  const [showWinPopup, setShowWinPopup] = useState<boolean>(false);
  const [showWinReveal, setShowWinReveal] = useState<boolean>(false);
  const [popupResult, setPopupResult] = useState<string>("");
  /** True when popup Claim may call complete (we have a valid `outcome_id` for UNCLAIMED; always true during spin-win flow until handled). */
  const [winPopupClaimReady, setWinPopupClaimReady] = useState<boolean>(true);
  const [claimSubmitting, setClaimSubmitting] = useState<boolean>(false);
  const [selectedSpin, setSelectedSpin] = useState<IncentiveAvailableSpinItem | null>(null);
  const [spinsLoading, setSpinsLoading] = useState<boolean>(false);
  const [spinRequestLoading, setSpinRequestLoading] = useState<boolean>(false);
  const [outcomeStatusFilter, setOutcomeStatusFilter] = useState<string>("ACTIVE");
  const [incentives, setIncentives] = useState<IncentiveAvailableSpinItem[]>([]);
  const [nextPageToken, setNextPageToken] = useState<string>("");
  const [hasMoreIncentives, setHasMoreIncentives] = useState<boolean>(false);
  const [loadingMoreIncentives, setLoadingMoreIncentives] = useState<boolean>(false);
  const [historyClaimLoadingId, setHistoryClaimLoadingId] = useState<string | null>(
    null
  );
  const loadMoreInFlightRef = useRef(false);
  const historySectionRef = useRef<HTMLDivElement>(null);
  const rotationDegRef = useRef<number>(0);
  const freeSpinRafRef = useRef<number | null>(null);
  const spinMotionSessionRef = useRef<SpinMotionSession | null>(null);
  const spinWinOutcomeIdRef = useRef<string | null>(null);
  /** Incentive id for claim URL (wheel spin or UNCLAIMED row). */
  const spinWinIncentiveIdRef = useRef<string | null>(null);
  /** Prevents stale UNCLAIMED outcome fetch from updating popup if user taps another row. */
  const unclaimedOutcomeRequestIdRef = useRef(0);
  const settleCoastRafRef = useRef<number | null>(null);
  const settleTimeoutRef = useRef<number | null>(null);
  const isSpinHiddenByFilter =
    outcomeStatusFilter === "CONSUMED" || outcomeStatusFilter === "EXPIRED";
  const dispatch = useDispatch<any>();

  const [expandedOutcomeIncentiveId, setExpandedOutcomeIncentiveId] =
    useState<string | null>(null);
  const [outcomeDetails, setOutcomeDetails] = useState<IncentiveOutcomeDetailResponse[]>([]);
  const [outcomeDetailsLoading, setOutcomeDetailsLoading] = useState<boolean>(false);
  const outcomeDetailsRequestIdRef = useRef(0);
  const { activeTabTextColorClass, pageTextColorClass } =
    getDynamicTextColors(userDetails);
  const whiteLabelLogo = getEnvVariable(domain, "logo");
  const inputBoxTextColorClass = dynamicTextColors?.inputBoxTextColorClass || '';
  const pageSize = 20;
  const wheelSections = useMemo(
    () =>
      (selectedSpin?.outcomes || []).map((outcome) => {
        const value = outcome.value;
        const formattedValue = Number.isInteger(value)
          ? `${value}`
          : `${value.toFixed(2)}`;
        return outcome.value_type === "PERCENT"
          ? `${formattedValue}%`
          : formattedValue;
      }),
    [selectedSpin]
  );
  const activeIncentivesCount = useMemo(
    () => incentives.filter((item) => item?.status === "ACTIVE").length,
    [incentives]
  );

  const displayWheelSections = useMemo(() => {
    if (activeIncentivesCount > 0) {
      return wheelSections;
    }
    const n = wheelSections.length > 0 ? wheelSections.length : 5;
    const shuffled = [...DEMO_WHEEL_LABEL_POOL].sort(
      () => Math.random() - 0.5
    );
    return Array.from(
      { length: n },
      (_, i) => shuffled[i % shuffled.length]
    );
  }, [activeIncentivesCount, wheelSections]);

  const generateRequestId = () => {
    if (window?.crypto?.randomUUID) {
      return window.crypto.randomUUID();
    }
    return `spin-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  };

  const formatDateTime = (time: number) => {
    return new Date(time).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  useEffect(() => {
    rotationDegRef.current = rotationDeg;
  }, [rotationDeg]);

  const stopFreeSpinLoop = useCallback(() => {
    if (freeSpinRafRef.current !== null) {
      cancelAnimationFrame(freeSpinRafRef.current);
      freeSpinRafRef.current = null;
    }
  }, []);

  const stopSettleCoastLoop = useCallback(() => {
    if (settleCoastRafRef.current !== null) {
      cancelAnimationFrame(settleCoastRafRef.current);
      settleCoastRafRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      stopFreeSpinLoop();
      stopSettleCoastLoop();
      if (settleTimeoutRef.current !== null) {
        window.clearTimeout(settleTimeoutRef.current);
      }
    };
  }, [stopFreeSpinLoop, stopSettleCoastLoop]);

  const loadSpins = useCallback(async (reset = true) => {
    if (reset) {
      setSpinsLoading(true);
    } else {
      if (loadingMoreIncentives || !hasMoreIncentives || loadMoreInFlightRef.current)
        return;
      loadMoreInFlightRef.current = true;
      setLoadingMoreIncentives(true);
    }
    try {
      const response = await BONUS_API.get("/incentives", {
        params: {
          type: "SPIN",
          status: outcomeStatusFilter,
          page_token: reset ? "" : nextPageToken,
          page_size: pageSize,
        },
      });
      const spinsList: IncentiveAvailableSpinItem[] =
        response?.data?.incentives || [];
      const fetchedNextPageToken = response?.data?.next_page_token || "";
      setNextPageToken(fetchedNextPageToken);
      setHasMoreIncentives(Boolean(fetchedNextPageToken));

      if (reset) {
        setIncentives(spinsList);
      } else {
        setIncentives((prev) => [...prev, ...spinsList]);
      }

      const needsDummyWheel =
        reset &&
        outcomeStatusFilter === "ACTIVE" &&
        (!spinsList.length ||
          !spinsList.find((spin) => (spin?.outcomes?.length ?? 0) > 0));

      if (needsDummyWheel) {
        // Provide placeholder wheel slices so the UI doesn't look empty.
        const dummySpin: IncentiveAvailableSpinItem = {
          user_incentive_id: "",
          transaction_id: "",
          policy_name: "",
          attempts_available: 0,
          expiry_at: 0,
          status: "ACTIVE",
          outcomes: [
            { reward_type: "DUMMY", value_type: "NUMBER", value: 10 },
            { reward_type: "DUMMY", value_type: "NUMBER", value: 20 },
            { reward_type: "DUMMY", value_type: "NUMBER", value: 30 },
            { reward_type: "DUMMY", value_type: "NUMBER", value: 40 },
            { reward_type: "DUMMY", value_type: "NUMBER", value: 50 },
          ],
        };

        setSelectedSpin(dummySpin);
        setHighlightedSection(null);
        setLastResult("");
        setShowWinPopup(false);
        setWinPopupClaimReady(true);
        return;
      }

      const firstSpinWithOutcomes =
        spinsList.find((spin) => spin?.outcomes?.length > 0) ||
        spinsList[0] ||
        null;
      if (reset) {
        setSelectedSpin(firstSpinWithOutcomes);
        setHighlightedSection(null);
        setLastResult("");
        setShowWinPopup(false);
        setWinPopupClaimReady(true);
      }
    } catch (e) {
      toast.error("Unable to load more. Please try again.");
      if (reset) {
        setIncentives([]);
        setSelectedSpin(null);
      }
    } finally {
      if (reset) {
        setSpinsLoading(false);
      } else {
        setLoadingMoreIncentives(false);
        loadMoreInFlightRef.current = false;
      }
    }
  }, [
    hasMoreIncentives,
    loadingMoreIncentives,
    nextPageToken,
    outcomeStatusFilter,
    pageSize,
  ]);

  useEffect(() => {
    void loadSpins(true);
  }, [outcomeStatusFilter]);

  useEffect(() => {
    setExpandedOutcomeIncentiveId(null);
    setOutcomeDetails([]);
    setOutcomeDetailsLoading(false);
  }, [outcomeStatusFilter]);

  useEffect(() => {
    if (
      (outcomeStatusFilter !== "CONSUMED" && outcomeStatusFilter !== "EXPIRED") ||
      spinsLoading
    ) {
      return;
    }
    const id = window.requestAnimationFrame(() => {
      historySectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
    return () => window.cancelAnimationFrame(id);
  }, [outcomeStatusFilter, spinsLoading]);

  const handleSpin = () => {
    if (isSpinHiddenByFilter) return;
    if (
      isSpinning ||
      spinRequestLoading ||
      wheelSections.length === 0 ||
      !selectedSpin?.user_incentive_id ||
      activeIncentivesCount <= 0
    ) {
      return;
    }

    const spinTarget = selectedSpin;
    const incentiveIdForBalanceRefresh = spinTarget?.user_incentive_id;
    const nSections = wheelSections.length;

    setIsSpinning(true);
    setHighlightedSection(null);
    setLastResult("");

    stopFreeSpinLoop();
    stopSettleCoastLoop();
    if (spinMotionSessionRef.current) {
      spinMotionSessionRef.current.cancelled = true;
    }
    spinWinOutcomeIdRef.current = null;
    spinWinIncentiveIdRef.current = null;
    setShowWinReveal(false);
    setShowWinPopup(false);
    setWinPopupClaimReady(true);

    const t0 = performance.now();
    const motionCtx: SpinMotionSession = {
      cancelled: false,
      phase: "free",
      lastNow: t0,
      spinT0: t0,
      pendingTargetIndex: null,
      resolvedIdx: 0,
      coastT0: 0,
      coastStart: 0,
      coastEnd: 0,
      coastDurationMs: SPIN_SETTLE_MS_MIN,
    };
    spinMotionSessionRef.current = motionCtx;

    const finishSpinError = (showMessage = true) => {
      motionCtx.cancelled = true;
      stopFreeSpinLoop();
      setIsSpinning(false);
      setSpinRequestLoading(false);
      if (showMessage) {
        toast.error("Could not process spin. Please try again.");
      }
    };

    setSpinRequestLoading(true);
    void BONUS_API.post<IncentiveSpinResponse>(
      `/incentives/${spinTarget.user_incentive_id}/spin`,
      { request_id: generateRequestId() }
    )
      .then((response) => {
        if (motionCtx.cancelled) return;

        const spinOutcome = response?.data;
        const spinReward = spinOutcome?.reward;
        const rewardAmount = spinReward
          ? getSpinRewardNumericAmount(spinReward)
          : undefined;
        if (
          !spinOutcome?.outcome_id ||
          !spinReward ||
          rewardAmount === undefined
        ) {
          finishSpinError();
          return;
        }

        const rewardType = getSpinRewardTypeKey(spinReward);
        const targetIndex = spinTarget.outcomes.findIndex((outcome) => {
          const valueMatch =
            Math.abs((outcome?.value ?? 0) - rewardAmount) < 0.000001;
          if (!valueMatch) return false;
          if (
            rewardType &&
            outcome?.reward_type &&
            outcome.reward_type !== rewardType
          ) {
            return false;
          }
          return true;
        });

        if (targetIndex < 0) {
          finishSpinError();
          return;
        }

        motionCtx.pendingTargetIndex = targetIndex;
        spinWinOutcomeIdRef.current = spinOutcome.outcome_id;
        spinWinIncentiveIdRef.current = spinTarget.user_incentive_id;

        setSelectedSpin((prev) =>
          prev
            ? {
                ...prev,
                attempts_available: Math.max(
                  (prev.attempts_available ?? 0) - 1,
                  0
                ),
              }
            : prev
        );
        setIncentives((prev) =>
          prev.map((item) =>
            item.user_incentive_id === spinTarget.user_incentive_id
              ? {
                  ...item,
                  attempts_available: Math.max(
                    (item.attempts_available ?? 0) - 1,
                    0
                  ),
                }
              : item
          )
        );
      })
      .catch(() => {
        if (motionCtx.cancelled) return;
        finishSpinError(true);
      });

    const spinStep = (now: number) => {
      if (motionCtx.cancelled) {
        freeSpinRafRef.current = null;
        return;
      }

      const deltaMs = Math.min(Math.max(now - motionCtx.lastNow, 0), 32);
      motionCtx.lastNow = now;

      if (
        motionCtx.phase === "free" &&
        motionCtx.pendingTargetIndex !== null &&
        motionCtx.pendingTargetIndex >= 0 &&
        now - motionCtx.spinT0 >= SPIN_MIN_FREE_SPIN_MS
      ) {
        rotationDegRef.current += FREE_SPIN_DEG_PER_MS * deltaMs;
        setRotationDeg(rotationDegRef.current);

        const idx = motionCtx.pendingTargetIndex;
        motionCtx.resolvedIdx = idx;
        motionCtx.pendingTargetIndex = null;
        setSpinRequestLoading(false);

        const pointerAngle = 360;
        const sectionAngle = 360 / nSections;
        const targetMidAngle = idx * sectionAngle + sectionAngle / 2;
        const currentRotation = rotationDegRef.current;
        const normalizedCurrent = ((currentRotation % 360) + 360) % 360;
        const finalNormalized = (pointerAngle - targetMidAngle + 360) % 360;
        const additionalNormalized =
          (finalNormalized - normalizedCurrent + 360) % 360;
        const fullRounds = 1 + Math.floor(Math.random() * 2);
        const totalAdditionalRotation = fullRounds * 360 + additionalNormalized;
        const nextRotation = currentRotation + totalAdditionalRotation;

        motionCtx.phase = "coast";
        motionCtx.coastT0 = now;
        motionCtx.coastStart = currentRotation;
        motionCtx.coastEnd = nextRotation;
        const settleFromVelocity =
          (SPIN_SETTLE_EASE_START_SLOPE * totalAdditionalRotation) /
          FREE_SPIN_DEG_PER_MS;
        motionCtx.coastDurationMs = Math.min(
          SPIN_SETTLE_MS_MAX,
          Math.max(SPIN_SETTLE_MS_MIN, settleFromVelocity * 1.06)
        );
      }

      if (motionCtx.phase === "free") {
        const nextRot =
          rotationDegRef.current + FREE_SPIN_DEG_PER_MS * deltaMs;
        rotationDegRef.current = nextRot;
        setRotationDeg(nextRot);
      }

      if (motionCtx.phase === "coast") {
        const t = Math.min(
          1,
          (now - motionCtx.coastT0) / motionCtx.coastDurationMs
        );
        const eased = easeOutCubic(t);
        const angle =
          motionCtx.coastStart +
          (motionCtx.coastEnd - motionCtx.coastStart) * eased;
        rotationDegRef.current = angle;
        setRotationDeg(angle);

        if (t >= 1) {
          rotationDegRef.current = motionCtx.coastEnd;
          setRotationDeg(motionCtx.coastEnd);
          freeSpinRafRef.current = null;
          spinMotionSessionRef.current = null;

          if (settleTimeoutRef.current !== null) {
            window.clearTimeout(settleTimeoutRef.current);
          }
          const resolvedIndex = motionCtx.resolvedIdx;
          settleTimeoutRef.current = window.setTimeout(() => {
            const resultIndex = resolvedIndex % nSections;
            setLastResult(wheelSections[resultIndex]);
            setHighlightedSection(resultIndex);
            setPopupResult(wheelSections[resultIndex]);
            setShowWinReveal(true);
            setIsSpinning(false);

            if (incentiveIdForBalanceRefresh) {
              void dispatch(getUserBalance() as any);
            }
          }, SPIN_POPUP_DELAY_MS);
          return;
        }
      }

      freeSpinRafRef.current = requestAnimationFrame(spinStep);
    };

    freeSpinRafRef.current = requestAnimationFrame(spinStep);
  };

  const runWinClaimRequest = useCallback(async (): Promise<boolean> => {
    const outcomeId = spinWinOutcomeIdRef.current;
    const incentiveId = spinWinIncentiveIdRef.current;
    if (!outcomeId || !incentiveId) return false;
    await BONUS_API.post<IncentiveSpinCompleteResponse>(
      `/incentives/${incentiveId}/outcomes/${outcomeId}/claim`,
      {}
    );
    return true;
  }, []);

  /** After spin + claim popup is done: disc, winner highlight, and result copy. */
  const resetWheelVisualState = useCallback(() => {
    rotationDegRef.current = 0;
    setRotationDeg(0);
    setHighlightedSection(null);
    setLastResult("");
    setPopupResult("");
  }, []);

  const handleClaimWinPopup = useCallback(async () => {
    if (!spinWinOutcomeIdRef.current || !spinWinIncentiveIdRef.current) {
      setWinPopupClaimReady(false);
      return;
    }
    setClaimSubmitting(true);
    try {
      await runWinClaimRequest();
      spinWinOutcomeIdRef.current = null;
      spinWinIncentiveIdRef.current = null;
      resetWheelVisualState();
      setShowWinReveal(false);
      setShowWinPopup(false);
      setWinPopupClaimReady(true);
      void loadSpins();
      void dispatch(getUserBalance() as any);
    } catch {
      console.error("Spin claim complete failed");
      toast.error("Could not claim reward. Please try again.");
    } finally {
      setClaimSubmitting(false);
    }
  }, [dispatch, loadSpins, resetWheelVisualState, runWinClaimRequest]);

  const handleCloseWinPopup = useCallback(async () => {
    if (claimSubmitting) return;
    const hadIds = Boolean(
      spinWinOutcomeIdRef.current && spinWinIncentiveIdRef.current
    );
    if (hadIds) {
      setClaimSubmitting(true);
      try {
        await runWinClaimRequest();
        void dispatch(getUserBalance() as any);
      } catch {
        console.error("Spin claim complete failed");
        toast.error("Could not claim reward. Please try again.");
      } finally {
        setClaimSubmitting(false);
      }
    }
    spinWinOutcomeIdRef.current = null;
    spinWinIncentiveIdRef.current = null;
    resetWheelVisualState();
    setShowWinReveal(false);
    setShowWinPopup(false);
    setWinPopupClaimReady(true);
    void loadSpins();
  }, [claimSubmitting, dispatch, loadSpins, resetWheelVisualState, runWinClaimRequest]);

  const handleUnclaimedHistoryClaim = useCallback(
    async (entry: IncentiveAvailableSpinItem) => {
      if (!entry.user_incentive_id || claimSubmitting) return;
      unclaimedOutcomeRequestIdRef.current += 1;
      const requestId = unclaimedOutcomeRequestIdRef.current;
      setHistoryClaimLoadingId(entry.user_incentive_id);
      try {
        const response = await BONUS_API.get<IncentiveOutcomeListResponse>(
          `/incentives/${entry.user_incentive_id}/outcomes`,
          {
          }
        );
        if (requestId !== unclaimedOutcomeRequestIdRef.current) return;

        const entries = response?.data?.entries || [];
        const pendingEntry =
          entries.find((item) => String(item?.status || "").toUpperCase() === "PENDING") ||
          entries[0];

        const rewardAmount = pendingEntry?.reward_amount;
        const rewardType = pendingEntry?.reward_type;
        const formatted =
          typeof rewardAmount === "number"
            ? Number.isInteger(rewardAmount)
              ? `${rewardAmount}`
              : rewardAmount.toFixed(2)
            : null;
        const typeUpper = String(rewardType || "").toUpperCase();
        const isPercent =
          typeUpper === "PERCENT" || typeUpper.includes("PERCENT");
        const rewardLabel =
          formatted !== null ? (isPercent ? `${formatted}%` : formatted) : "Reward";

        // UNCLAIMED: complete API must use outcome_id from this outcome response only.
        const outcomeIdFromOutcome = pendingEntry
          ? getOutcomeEntryId(pendingEntry)
          : null;
        spinWinOutcomeIdRef.current = outcomeIdFromOutcome;
        spinWinIncentiveIdRef.current = entry.user_incentive_id;
        setPopupResult(rewardLabel);
        setShowWinReveal(false);
        setWinPopupClaimReady(Boolean(outcomeIdFromOutcome));
        setShowWinPopup(true);
      } catch {
        if (requestId !== unclaimedOutcomeRequestIdRef.current) return;
        console.error("Spin UNCLAIMED outcome fetch failed");
        toast.error("Unable to load reward details. Please try again.");
        spinWinOutcomeIdRef.current = null;
        spinWinIncentiveIdRef.current = null;
        setPopupResult("Reward");
        setShowWinReveal(false);
        setWinPopupClaimReady(false);
        setShowWinPopup(true);
      } finally {
        if (requestId === unclaimedOutcomeRequestIdRef.current) {
          setHistoryClaimLoadingId(null);
        }
      }
    },
    [claimSubmitting]
  );

  useEffect(() => {
    if (!showWinReveal) return;
    const id = window.setTimeout(() => {
      setShowWinReveal(false);
      setShowWinPopup(true);
    }, SPIN_WIN_REVEAL_MS);
    return () => window.clearTimeout(id);
  }, [showWinReveal]);

  const fetchOutcomeDetailsForIncentive = useCallback(
    async (user_incentive_id: string) => {
      outcomeDetailsRequestIdRef.current += 1;
      const requestId = outcomeDetailsRequestIdRef.current;
      setOutcomeDetailsLoading(true);
      try {
        const params: Record<string, string> = {
        };

        const response = await BONUS_API.get<IncentiveOutcomeListResponse>(
          `/incentives/${user_incentive_id}/outcomes`,
          { params }
        );

        if (outcomeDetailsRequestIdRef.current === requestId) {
          setOutcomeDetails(response?.data?.entries || []);
        }
      } catch {
        if (outcomeDetailsRequestIdRef.current === requestId) {
          toast.error("Unable to load outcome details. Please try again.");
          setOutcomeDetails([]);
        }
      } finally {
        if (outcomeDetailsRequestIdRef.current === requestId) {
          setOutcomeDetailsLoading(false);
        }
      }
    },
    []
  );

  const handleOutcomeDetailsToggle = useCallback(
    async (user_incentive_id: string) => {
      if (expandedOutcomeIncentiveId === user_incentive_id) {
        setExpandedOutcomeIncentiveId(null);
        setOutcomeDetails([]);
        return;
      }

      setExpandedOutcomeIncentiveId(user_incentive_id);
      await fetchOutcomeDetailsForIncentive(user_incentive_id);
    },
    [expandedOutcomeIncentiveId, fetchOutcomeDetailsForIncentive]
  );

  return (
    <div className="spin-tab mt-3">
      <div
        className={`spin-tab__spin-panel bg-inputbox-color ${pageTextColorClass}`}
      >
        {outcomeStatusFilter === "ACTIVE" && activeIncentivesCount > 0 && (
          <div className="spin-tab__subtitle">
            Try your luck and win exclusive rewards.
          </div>
        )}

        {outcomeStatusFilter === "ACTIVE" ? (
          <div className="spin-tab__wheel-col">
            {activeIncentivesCount === 0 && !spinsLoading && (
              <p className="spin-tab__deposit-note">
                Please deposit and get a chance to spin the wheel.
              </p>
            )}
            <div
              className={`spin-tab__wheel-stage${
                showWinReveal ? " spin-tab__wheel-stage--win-reveal" : ""
              }${
                showWinPopup && !showWinReveal
                  ? " spin-tab__wheel-stage--claim-popup"
                  : ""
              }`}
            >
              <div className="spin-tab__wheel-fx" aria-hidden>
                <span className="spin-tab__wheel-fx__dot" />
                <span className="spin-tab__wheel-fx__dot" />
                <span className="spin-tab__wheel-fx__dot" />
                <span className="spin-tab__wheel-fx__dot" />
                <span className="spin-tab__wheel-fx__dot" />
                <span className="spin-tab__wheel-fx__dot" />
                <span className="spin-tab__wheel-fx__balloon" />
                <span className="spin-tab__wheel-fx__balloon" />
                <span className="spin-tab__wheel-fx__balloon" />
                <span className="spin-tab__wheel-fx__coin" />
                <span className="spin-tab__wheel-fx__coin" />
                <span className="spin-tab__wheel-fx__coin" />
                <span className="spin-tab__wheel-fx__coin" />
              </div>

              {showWinReveal && !showWinPopup && (
                <ConfettiCelebrationBlast variant="stage" />
              )}

              <div className="spin-tab__wheel-bulbs" aria-hidden>
                {Array.from({ length: WHEEL_BULB_COUNT }, (_, i) => (
                  <span
                    key={`wheel-bulb-${i}`}
                    className="spin-tab__wheel-bulb-arm"
                    style={
                      {
                        "--wb-angle": `${(360 / WHEEL_BULB_COUNT) * i}deg`,
                        "--wb-delay": `${((i * 0.13) % 1.04).toFixed(3)}s`,
                      } as React.CSSProperties
                    }
                  >
                    <span className="spin-tab__wheel-bulb-core" />
                  </span>
                ))}
              </div>

              <div
                className="spin-tab__disc"
                style={
                  {
                    "--spin-rotation": `${rotationDeg}deg`,
                  } as React.CSSProperties
                }
              >
                {displayWheelSections.map((section, index) => {
                  const sliceAngle = 360 / displayWheelSections.length;
                  const midAngle = index * sliceAngle + sliceAngle / 2;
                  const isWinner =
                    activeIncentivesCount > 0 && highlightedSection === index;

                  return (
                    <div
                      key={`wheel-${index}-${section}`}
                      className="spin-tab__section-anchor"
                      style={
                        {
                          "--section-mid": `${midAngle}deg`,
                        } as React.CSSProperties
                      }
                    >
                      <div
                        className={`spin-tab__section-label ${
                          isWinner
                            ? "spin-tab__section-label--winner"
                            : `spin-tab__section-label--default spin-tab__section-label--slice-${index % 5}`
                        }`}
                      >
                        {section}
                      </div>
                    </div>
                  );
                })}
                <div className="spin-tab__disc-gloss" />
                {highlightedSection !== null &&
                  activeIncentivesCount > 0 &&
                  displayWheelSections.length > 0 && (
                  <div
                    className="spin-tab__highlight"
                    style={
                      {
                        "--hl-start": `${highlightedSection * (360 / displayWheelSections.length)}deg`,
                        "--hl-end": `${(highlightedSection + 1) * (360 / displayWheelSections.length)}deg`,
                      } as React.CSSProperties
                    }
                  />
                )}
              </div>

              <div className="spin-tab__hub">
                <div className="spin-tab__pointer-wrap">
                  <div className="spin-tab__pointer-triangle" />
                  <div className="spin-tab__pointer-stem" />
                </div>
                <div className="spin-tab__logo-ring">
                  <div className="spin-tab__logo-ring-inner" />
                  <img
                    src={whiteLabelLogo}
                    alt="Brand logo"
                    className="spin-tab__logo-img"
                  />
                </div>
              </div>

              <div className="spin-tab__wheel-stand" aria-hidden>
                <div className="spin-tab__wheel-stand__neck" />
                <div className="spin-tab__wheel-stand__collar" />
                <div className="spin-tab__wheel-stand__base" />
              </div>

              {showWinReveal && (
                <div className="spin-tab__win-reveal" aria-live="polite">
                  <div
                    className="spin-tab__win-reveal__backdrop"
                    aria-hidden
                  />
                  <div className="spin-tab__win-reveal__content">
                    <img
                      src={whiteLabelLogo}
                      alt=""
                      className="spin-tab__win-reveal__logo"
                    />
                    <div className="spin-tab__win-reveal__text">
                      <span className="spin-tab__win-reveal__kicker">
                        You won
                      </span>
                      <span className="spin-tab__win-reveal__prize">
                        {popupResult}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {!isSpinHiddenByFilter && activeIncentivesCount > 0 && (
              <>
                <button
                  type="button"
                  className={`spin-tab__spin-btn border border-solid border-border-color bg-border-color ${activeTabTextColorClass}`}
                  onClick={handleSpin}
                  disabled={
                    isSpinning ||
                    spinRequestLoading ||
                    wheelSections.length === 0 ||
                    spinsLoading
                  }
                  aria-busy={spinRequestLoading || isSpinning}
                >
                  {spinsLoading ? (
                    "Loading..."
                  ) : spinRequestLoading || isSpinning ? (
                    <>
                      <span className="sr-only">
                        {spinRequestLoading
                          ? "Processing spin"
                          : "Wheel spinning"}
                      </span>
                      <span
                        className="spin-tab__spin-btn-bulbs"
                        aria-hidden
                      >
                        <span className="spin-tab__spin-btn-bulb spin-tab__spin-btn-bulb--1" />
                        <span className="spin-tab__spin-btn-bulb spin-tab__spin-btn-bulb--2" />
                        <span className="spin-tab__spin-btn-bulb spin-tab__spin-btn-bulb--3" />
                      </span>
                    </>
                  ) : (
                    "Spin Now"
                  )}
                </button>

                <div className="spin-tab__hint">
                  {selectedSpin
                    ? `Attempts left: ${activeIncentivesCount}`
                    : "No spin outcomes available right now."}
                </div>
              </>
            )}
            {activeIncentivesCount > 0 && lastResult && !isSpinning && (
              <div className="spin-tab__result-line">
                You won:{" "}
                <span className="spin-tab__result-value">{lastResult}</span>
              </div>
            )}
          </div>
        ) : null}

        <div className="filters flex flex-wrap gap-3 mt-0 mb-2">
          <div className="w-[48%]">
            <div className="text-xs font-semibold mb-2 opacity-80">Status</div>
            <select
              id="spin-outcome-status"
              value={outcomeStatusFilter}
              onChange={(e) => setOutcomeStatusFilter(e.target.value)}
              className={`spin-tab__status-filter flex items-center justify-between w-full px-3 py-2 text-xs font-medium whitespace-nowrap rounded-lg border border-solid bg-inputbox-color ${inputBoxTextColorClass === "dark-text" ? "text-black" : "text-white"}`}
            >
              <option value="ACTIVE">ACTIVE</option>
              <option value="CONSUMED">CLAIMED</option>
              <option value="PENDING">UNCLAIMED</option>
              <option value="EXPIRED">EXPIRED</option>
            </select>
          </div>
        </div>

        <div className={`spin-tab__history-title mt-2 ${pageTextColorClass}`}>
          Details
        </div>

        {!incentives.length && !spinsLoading ? (
          <div
            ref={historySectionRef}
            className={`spin-tab__history-text mt-2 ${pageTextColorClass}`}
          >
            No Spin Detail Available.
          </div>
        ) : spinsLoading ? (
          <div
            ref={historySectionRef}
            className={`spin-tab__history-text mt-2 ${pageTextColorClass}`}
          >
            Loading outcomes...
          </div>
        ) : (
          <>
              {incentives.map((entry, index) => (
                (() => {
                  const rowStatus = String(entry.status || "").toUpperCase();
                  const isActiveRow = rowStatus === "ACTIVE";
                  const hideHistoryExpand =
                    isActiveRow || outcomeStatusFilter === "EXPIRED";
                  return (
                <div
                  key={`${entry.user_incentive_id}-${index}`}
                  ref={index === 0 ? historySectionRef : undefined}
                  className={`rounded-lg border border-border-color/40 bg-black/20 p-3 ${pageTextColorClass} ${
                    index === 0 ? "mt-3" : "mt-2"
                  }`}
                >
                  <div className="spin-tab__history-row flex w-full items-start gap-2">
                    <button
                      type="button"
                      className="spin-tab__history-row-main min-w-0 flex-1 rounded-md p-1 -m-1 text-left outline-none transition-colors hover:bg-black/15 focus-visible:ring-2 focus-visible:ring-border-color/60 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent disabled:cursor-not-allowed disabled:opacity-60"
                      onClick={() => {
                        if (!hideHistoryExpand) {
                          handleOutcomeDetailsToggle(entry.user_incentive_id);
                        }
                      }}
                      disabled={
                        hideHistoryExpand ||
                        spinsLoading ||
                        (outcomeDetailsLoading &&
                          expandedOutcomeIncentiveId !==
                            entry.user_incentive_id)
                      }
                      aria-expanded={
                        expandedOutcomeIncentiveId === entry.user_incentive_id
                      }
                    >
                      <div className="text-xs opacity-80">
                        Expiry: {formatDateTime(entry.expiry_at)}
                      </div>
                      <div className="mt-1 text-xs opacity-90">
                        Status: {entry.status}
                      </div>
                      <div className="mt-1 text-xs opacity-80">
                        Transaction ID: {entry.transaction_id || "-"}
                      </div>
                      <span className="sr-only">
                        {expandedOutcomeIncentiveId === entry.user_incentive_id
                          ? "Hide outcome details"
                          : "Show outcome details"}
                      </span>
                    </button>
                    {outcomeStatusFilter === "PENDING" && (
                      <button
                        type="button"
                        className="spin-tab__history-claim-btn shrink-0 self-center"
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleUnclaimedHistoryClaim(entry);
                        }}
                        disabled={
                          claimSubmitting ||
                          historyClaimLoadingId !== null ||
                          spinsLoading
                        }
                      >
                        {historyClaimLoadingId === entry.user_incentive_id
                          ? "Loading..."
                          : "Claim"}
                      </button>
                    )}
                    {!hideHistoryExpand && (
                    <button
                      type="button"
                      className="spin-tab__history-chevron mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border border-border-color/45 bg-black/20 opacity-90 outline-none transition-colors hover:bg-black/25 focus-visible:ring-2 focus-visible:ring-border-color/60 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent disabled:cursor-not-allowed disabled:opacity-60"
                      onClick={() =>
                        handleOutcomeDetailsToggle(entry.user_incentive_id)
                      }
                      disabled={
                        spinsLoading ||
                        (outcomeDetailsLoading &&
                          expandedOutcomeIncentiveId !==
                            entry.user_incentive_id)
                      }
                      aria-expanded={
                        expandedOutcomeIncentiveId === entry.user_incentive_id
                      }
                      aria-label={
                        expandedOutcomeIncentiveId === entry.user_incentive_id
                          ? "Hide outcome details"
                          : "Show outcome details"
                      }
                    >
                      {outcomeDetailsLoading &&
                      expandedOutcomeIncentiveId === entry.user_incentive_id ? (
                        <span className="text-[10px] font-semibold uppercase tracking-wide opacity-80">
                          …
                        </span>
                      ) : expandedOutcomeIncentiveId ===
                        entry.user_incentive_id ? (
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.25"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden
                        >
                          <path d="M18 15l-6-6-6 6" />
                        </svg>
                      ) : (
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.25"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden
                        >
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      )}
                    </button>
                    )}
                  </div>

                  {expandedOutcomeIncentiveId === entry.user_incentive_id && (
                    <div className="mt-3">
                      {outcomeDetailsLoading ? (
                        <div className="spin-tab__history-text">
                          Loading outcome details...
                        </div>
                      ) : outcomeDetails.length === 0 ? (
                        <div className="spin-tab__history-text">
                          No settled outcomes found.
                        </div>
                      ) : (
                        <div className="flex flex-col gap-2">
                          {outcomeDetails.map((outcome, outcomeIndex) => (
                            <div
                              key={`${outcome.user_incentive_id}-${outcome.created_at}-${outcomeIndex}`}
                              className="rounded-lg border border-border-color/40 bg-black/10 p-3"
                            >
                              <div className="text-xs opacity-80">
                                {formatDateTime(outcome.created_at)}
                              </div>
                              <div className="mt-1 text-sm font-semibold">
                                Reward:{" "}
                                {Number.isInteger(outcome.reward_amount)
                                  ? outcome.reward_amount
                                  : outcome.reward_amount.toFixed(2)}{" "}
                                ({outcome.reward_type})
                              </div>
                              <div className="text-xs opacity-90">
                                Required wager:{" "}
                                {Number.isInteger(outcome.required_wager)
                                  ? outcome.required_wager
                                  : outcome.required_wager.toFixed(2)}
                              </div>
                              <div className="text-xs opacity-90">
                                Status: {outcome.status}
                              </div>
                            </div>
              ))}
            </div>
          )}
        </div>
                  )}
                </div>
                  );
                })()
              ))}
              {hasMoreIncentives && (
                <button
                  type="button"
                  className={`mt-2 self-center rounded-md border border-border-color px-3 py-1 text-xs ${pageTextColorClass}`}
                  onClick={() => {
                    void loadSpins(false);
                  }}
                  disabled={loadingMoreIncentives}
                >
                  {loadingMoreIncentives ? "Loading..." : "Load more"}
                </button>
              )}
          </>
        )}

        {showWinPopup && !isSpinning && !showWinReveal && (
          <div
            className="spin-tab__popup-backdrop"
            role="dialog"
            aria-modal="true"
            aria-labelledby="spin-win-popup-title"
          >
            <ConfettiCelebrationBlast variant="popup" />
            <div className="spin-tab__popup-card">
              <button
                type="button"
                className="spin-tab__popup-close"
                onClick={() => {
                  void handleCloseWinPopup();
                }}
                disabled={claimSubmitting}
                aria-busy={claimSubmitting}
              >
                x
              </button>
              <div className="spin-tab__popup-icon">
                <img src={partyPopIcon} alt="" />
              </div>
              <div className="spin-tab__popup-kicker">Congratulations</div>
              <div
                id="spin-win-popup-title"
                className="spin-tab__popup-title"
              >
                {popupResult ? `You earned ${popupResult}` : "Loading your reward…"}
              </div>
              <div className="spin-tab__popup-sub">
                {popupResult
                  ? "Reward will be added to your wallet"
                  : "Please wait"}
              </div>
              <button
                type="button"
                className="spin-tab__popup-cta"
                onClick={() => {
                  void handleClaimWinPopup();
                }}
                disabled={claimSubmitting || !winPopupClaimReady}
              >
                {claimSubmitting ? "Claiming..." : "Congratulations"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpinTab;